import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CLINIC } from "@/lib/brand";
import { CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const nav = useNavigate();
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) { toast.error("Missing or invalid reset token"); return; }
    if (next.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (next !== confirm) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: next });
      setDone(true);
      setTimeout(() => nav("/login", { replace: true }), 1800);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[color:var(--bg-marketing)]">
      {done ? (
        <div className="w-full max-w-md bg-white border border-[color:var(--border)] rounded-2xl p-8 shadow-sm text-center" data-testid="reset-done-card">
          <div className="w-14 h-14 rounded-full bg-green-100 grid place-items-center mx-auto">
            <CheckCircle2 className="w-7 h-7 text-green-600" strokeWidth={1.6} />
          </div>
          <h2 className="font-heading text-2xl text-[color:var(--ink)] mt-5">Password updated</h2>
          <p className="text-sm text-[color:var(--muted)] mt-2 leading-relaxed">Redirecting you to sign in…</p>
        </div>
      ) : (
        <form
          onSubmit={submit}
          className="w-full max-w-md bg-white border border-[color:var(--border)] rounded-2xl p-8 shadow-sm"
          data-testid="reset-password-form"
        >
          <div className="flex items-center gap-3 mb-5">
            <img src={CLINIC.logoUrl} alt={CLINIC.name} className="w-10 h-10 rounded-full ring-1 ring-[color:var(--primary-light)]" />
            <div>
              <p className="text-[10px] tracking-[0.2em] uppercase text-[color:var(--muted)]">Reset password</p>
              <h1 className="font-heading text-2xl text-[color:var(--ink)] font-medium leading-tight">Choose a new password</h1>
            </div>
          </div>

          {!token && (
            <p className="text-sm text-[color:var(--error)] mb-4" data-testid="reset-missing-token">
              The reset link is missing its security token. Please request a new email.
            </p>
          )}

          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">New password</label>
              <Input
                type="password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                className="mt-1.5"
                required
                data-testid="reset-new"
              />
            </div>
            <div>
              <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">Confirm new password</label>
              <Input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-1.5"
                required
                data-testid="reset-confirm"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading || !token}
            className="w-full mt-6 rounded-full bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white h-11"
            data-testid="reset-submit"
          >
            {loading ? "Updating…" : "Update password"}
          </Button>

          <Link to="/login" className="block mt-5 text-sm text-[color:var(--muted)] hover:text-[color:var(--ink)] text-center" data-testid="reset-back-link">
            ← Back to sign in
          </Link>
        </form>
      )}
    </div>
  );
}
